<script setup lang="ts">
/**
 * Writing a menu worth having again (SPEC §6.16, §8.16).
 *
 * Name, description, and the shared menu editor. The whole tree saves in one
 * call, because that is what an editor has: somebody rearranges four items and
 * renames a section, then presses save once.
 *
 * Leaving with unsaved work is guarded. An organizer who has just spent ten
 * minutes on a menu and taps the back button should be asked, not obeyed.
 */
import { computed, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import MenuEditor from '@/components/MenuEditor.vue'
import type { MenuSectionDraft } from '@/components/menuEditor'
import { ApiError, endpoints, type MealTemplateResponse, type SaveMealTemplateRequest } from '@/api'
import { asNumber } from '@/utils/numbers'

const route = useRoute()
const router = useRouter()

const groupId = computed(() => String(route.params.groupId))
const templateId = computed(() => String(route.params.templateId))
const isNew = computed(() => templateId.value === 'new')

const name = ref('')
const description = ref('')
const sections = ref<MenuSectionDraft[]>([])

const loading = ref(!isNew.value)
const saving = ref(false)
const error = ref<string | null>(null)
const notice = ref<string | null>(null)
const fieldErrors = ref<Record<string, string[]>>({})

/** What was last saved, to tell an edit from a load. */
const saved = ref('')

const dirty = computed(() => snapshot() !== saved.value)

const leaving = ref<(() => void) | null>(null)

function snapshot(): string {
  return JSON.stringify({
    name: name.value,
    description: description.value,
    sections: sections.value,
  })
}

function describe(caught: unknown): string {
  return (caught instanceof ApiError && caught.problem?.title) || 'That did not work.'
}

/**
 * Pulls the field errors out of a validation problem.
 *
 * The server names the field that is wrong — "Sections[1].Items[0].Name" —
 * which is more use under the control than at the top of the page.
 */
function fieldsFrom(caught: unknown): Record<string, string[]> {
  if (!(caught instanceof ApiError)) return {}

  const problem = caught.problem as { errors?: Record<string, string[]> } | undefined

  return problem?.errors ?? {}
}

function adopt(template: MealTemplateResponse) {
  name.value = template.name
  description.value = template.description ?? ''
  sections.value = template.sections.map((section) => ({
    id: section.id,
    name: section.name,
    items: section.items.map((item) => ({
      id: item.id,
      name: item.name,
      notes: item.notes ?? null,
      participationMode:
        item.participationMode as MenuSectionDraft['items'][number]['participationMode'],
      quantityNeeded: asNumber(item.quantityNeeded),
      unitLabel: item.unitLabel ?? null,
      isFlexible: item.isFlexible,
      allowOverClaim: item.allowOverClaim,
    })),
  }))

  saved.value = snapshot()
}

async function load() {
  if (isNew.value) {
    saved.value = snapshot()
    return
  }

  loading.value = true
  error.value = null

  try {
    adopt(await endpoints.getMealTemplate(groupId.value, templateId.value))
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    loading.value = false
  }
}

function body(): SaveMealTemplateRequest {
  return {
    name: name.value,
    description: description.value || null,
    sections: sections.value.map((section) => ({
      id: section.id,
      name: section.name,
      items: section.items.map((item) => ({
        id: item.id,
        name: item.name,
        notes: item.notes,
        participationMode: item.participationMode,
        quantityNeeded: item.quantityNeeded,
        unitLabel: item.unitLabel,
        isFlexible: item.isFlexible,
        allowOverClaim: item.allowOverClaim,
      })),
    })),
  }
}

async function save() {
  saving.value = true
  error.value = null
  notice.value = null
  fieldErrors.value = {}

  try {
    const result = isNew.value
      ? await endpoints.createMealTemplate(groupId.value, body())
      : await endpoints.updateMealTemplate(groupId.value, templateId.value, body())

    adopt(result)
    notice.value = 'Saved.'

    if (isNew.value) {
      // Replace rather than push: going back from here should reach the list,
      // not an empty editor that would create a second template.
      await router.replace({
        name: 'meal-template',
        params: { groupId: groupId.value, templateId: result.id },
      })
    }
  } catch (caught) {
    error.value = describe(caught)
    fieldErrors.value = fieldsFrom(caught)
  } finally {
    saving.value = false
  }
}

function back() {
  void router.push({ name: 'meal-templates', params: { groupId: groupId.value } })
}

onBeforeRouteLeave((_to, _from, next) => {
  if (!dirty.value) {
    next()
    return
  }

  leaving.value = () => next()
})

function discard() {
  const go = leaving.value

  leaving.value = null
  saved.value = snapshot()
  go?.()
}

watch(templateId, () => void load())

void load()
</script>

<template>
  <div class="editor">
    <header class="editor__head">
      <v-btn variant="text" density="comfortable" prepend-icon="mdi-arrow-left" @click="back">
        Templates
      </v-btn>

      <v-btn
        :loading="saving"
        :disabled="loading || name.trim() === ''"
        color="primary"
        variant="flat"
        @click="save"
      >
        Save
      </v-btn>
    </header>

    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>
    <v-alert v-if="notice" type="success" variant="tonal" class="mb-4">{{ notice }}</v-alert>

    <v-skeleton-loader v-if="loading" type="article" />

    <template v-else>
      <v-text-field
        v-model="name"
        :error-messages="fieldErrors['Name']"
        label="Template name"
        placeholder="Lasagna Dinner"
        variant="outlined"
        density="comfortable"
      />

      <v-textarea
        v-model="description"
        label="Description"
        placeholder="For the big Sunday."
        rows="2"
        variant="outlined"
        density="comfortable"
        hide-details
        class="mb-4"
      />

      <h2 class="editor__section-title">Menu</h2>

      <MenuEditor v-model="sections" :disabled="saving" noun="template" />
    </template>

    <v-dialog
      :model-value="leaving !== null"
      max-width="28rem"
      @update:model-value="leaving = null"
    >
      <v-card>
        <v-card-title tag="h2">Leave without saving?</v-card-title>
        <v-card-text>Your changes to this menu will be lost.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="leaving = null">Keep editing</v-btn>
          <v-btn variant="flat" color="primary" @click="discard">Leave</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.editor {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.editor__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.editor__section-title {
  font-size: 1.125rem;
  margin: 0.5rem 0;
}
</style>
